import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/authContext";
import Pagination from "@mui/material/Pagination";
import AddEditPatient from "../utils/AddEditPatient";
import { documentsPerPage, tagWeight, appName } from "../globals";
import { useSidebar } from "../contexts/SidebarContext";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { UserRoundPlus, UserRoundSearch, UserRoundPen, Pin, SquareChevronRight, Share2, Mic } from "lucide-react";
import LinearProgress from "@mui/material/LinearProgress";
import { capitalizePronoun, calculateAge, getSortIcon, getTrendIcon, sortPatients, renderPatientTags, renderRiskBadge, renderHistorySummary, handleSort as handleSortUtil } from "../utils/PatientsUtils";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import { usePatientDirectory } from "../hooks/usePatientDirectory";
import { setPatientPinPreference } from "./patientDirectoryService";

const PatientsList = ({ showOnlyPinned = false, pageTitle = "My Patients", welcomeMessage = null, showSortControls = true, showContactFrequency = true }) => {
  const listRef = useRef(null);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;
  const navigate = useNavigate();
  const [isEditPatientOpen, setEditPatientOpen] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { patients: directoryPatients, isLoading } = usePatientDirectory(userId);
  const { sidebarCollapsed } = useSidebar();
  const { flags } = useFeatureFlags();
  const [pinnedEffect, setPinnedEffect] = useState({});
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [fadingPatients, setFadingPatients] = useState({});
  const [fadeTimeouts, setFadeTimeouts] = useState({});

  useEffect(() => {
    setPatients(
      directoryPatients.filter((patient) => !showOnlyPinned || patient.pin === true)
    );
  }, [directoryPatients, showOnlyPinned]);

  // Listen for patient updates to refresh the list
  useEffect(() => {
    const handlePatientUpdate = (event) => {
      const { patientId: updatedPatientId, patientData: updatedPatientData } = event.detail || {};
      setPatients((prevPatients) => prevPatients.map((patient) => (patient.id === updatedPatientId ? { ...patient, ...updatedPatientData } : patient)));
    };

    const handlePatientCreated = (event) => {
      const { patientData: newPatientData } = event.detail || {};

      if (newPatientData) {
        // Only add to Dashboard if it's pinned, or add to Patients regardless
        if (!showOnlyPinned || (showOnlyPinned && newPatientData.pin)) {
          setPatients((prevPatients) => (
            prevPatients.some((patient) => patient.id === newPatientData.id)
              ? prevPatients
              : [...prevPatients, newPatientData]
          ));
        }
      }
    };

    window.addEventListener("patientUpdated", handlePatientUpdate);
    window.addEventListener("patientCreated", handlePatientCreated);

    return () => {
      window.removeEventListener("patientUpdated", handlePatientUpdate);
      window.removeEventListener("patientCreated", handlePatientCreated);
    };
  }, [showOnlyPinned]);

  const handlePatientClick = (patient) => {
    navigate("/patient", { state: { patientId: patient.id, from: "/patients" } });
  };

  const handleRecordClick = (e, patient) => {
    e.stopPropagation();
    navigate("/patient", { state: { patientId: patient.id, pendingAction: "record", from: "/patients" } });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePinClick = async (patient) => {
    try {
      setPinnedEffect((prev) => ({ ...prev, [patient.id]: true }));
      const newPinStatus = !patient.pin;

      await setPatientPinPreference(userId, patient.id, newPinStatus);
      setPatients((prevPatients) => prevPatients.map((p) => (p.id === patient.id ? { ...p, pin: newPinStatus } : p)));

      // If we're in dashboard view (showOnlyPinned) and unpinning
      if (showOnlyPinned && !newPinStatus) {
        // Start fade effect
        setFadingPatients((prev) => ({ ...prev, [patient.id]: true }));
        
        // Set timeout to remove patient after 10 seconds
        const timeoutId = setTimeout(() => {
          setPatients((prevPatients) => prevPatients.filter(p => p.id !== patient.id));
          setFadingPatients((prev) => {
            const newState = { ...prev };
            delete newState[patient.id];
            return newState;
          });
          setFadeTimeouts((prev) => {
            const newState = { ...prev };
            delete newState[patient.id];
            return newState;
          });
        }, 10000);
        
        setFadeTimeouts((prev) => ({ ...prev, [patient.id]: timeoutId }));
      }
      
      // If we're repinning (newPinStatus is true), cancel any existing fade timeout
      if (newPinStatus && fadeTimeouts[patient.id]) {
        clearTimeout(fadeTimeouts[patient.id]);
        setFadeTimeouts((prev) => {
          const newState = { ...prev };
          delete newState[patient.id];
          return newState;
        });
        setFadingPatients((prev) => {
          const newState = { ...prev };
          delete newState[patient.id];
          return newState;
        });
      }

      setTimeout(() => {
        setPinnedEffect((prev) => ({ ...prev, [patient.id]: false }));
      }, 600);
    } catch (error) {
      console.error("Error toggling pin status:", error);
      setPinnedEffect((prev) => ({ ...prev, [patient.id]: false }));
    }
  };

  const handleSort = (field) => {
    handleSortUtil(field, sortField, sortDirection, setSortField, setSortDirection);
  };

  // Memoize sort+filter so it only recomputes when inputs change — not on every
  // render (e.g. unrelated state like pin animations or pagination clicks).
  const filteredPatients = useMemo(
    () => sortPatients(patients, sortField, sortDirection, searchQuery),
    [patients, sortField, sortDirection, searchQuery]
  );
  const pageCount = Math.max(1, Math.ceil(filteredPatients.length / documentsPerPage));
  const visiblePage = Math.min(currentPage, pageCount);
  useLayoutEffect(() => {
    // The pager is below the rows. Its old scroll offset can hide a short
    // filtered result behind the fixed list header even on the correct page.
    const scrollContainer = listRef.current?.closest('.app-shell-content');
    if (scrollContainer) scrollContainer.scrollTop = 0;
  }, [searchQuery, visiblePage]);
  useEffect(() => {
    setCurrentPage(page => Math.min(page, pageCount));
  }, [pageCount]);
  const currentPatients = useMemo(() => {
    const indexOfLastPatient = visiblePage * documentsPerPage;
    const indexOfFirstPatient = indexOfLastPatient - documentsPerPage;
    return filteredPatients.slice(indexOfFirstPatient, indexOfLastPatient);
  }, [filteredPatients, visiblePage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const renderEmptyState = () => {
    if (searchQuery) {
      return <span className="error-message">Sorry, there are no patients matching your search.</span>;
    }

    if (showOnlyPinned) {
      return (
        <>
          Welcome to {appName}!<br />
          <span className="empty-state-hint">To get started, click the "Add Patient" button above.</span>
        </>
      );
    }

    return "No patients to display. Add one above...";
  };

  // Clean up timeouts on component unmount
  useEffect(() => {
    return () => {
      Object.values(fadeTimeouts).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, [fadeTimeouts]);

  return (
    <div ref={listRef} className={`page-container${sidebarCollapsed ? " wide" : ""}`}>
      <div className={`header-container ${showOnlyPinned ? "dashboard-header" : "patients-header"}${sidebarCollapsed ? " wide" : ""}`}>
        <div className="header-left">
          <div className="page-title">{pageTitle}</div>
        </div>

        <div className="header-right">
          <div className="header-search">
            <div className="home-header-search-wrapper">
              <Tooltip title="Search patients" arrow>
                <UserRoundSearch className="header-search-icon" />
              </Tooltip>
              <input type="text" placeholder="Search Patients" value={searchQuery} onChange={handleSearchChange} className="header-search-input userback-ignore" />
            </div>
          </div>
          <div className="header-button" onClick={() => setEditPatientOpen(true)}>
            <Tooltip title="Add Patient" arrow>
              <UserRoundPlus className="header-icon" />
            </Tooltip>
            Add Patient
          </div>
        </div>
        <AddEditPatient 
          setPatients={null}  // Let patientCreated event handle new patients to avoid duplicates
          isEditPatientOpen={isEditPatientOpen} 
          setEditPatientOpen={(open) => {
            setEditPatientOpen(open);
            if (!open) setEditingPatientId(null);
          }} 
          patientId={editingPatientId}
          onPatientUpdate={(updatedData) => {
            setPatients((prev) => prev.map((p) => 
              p.id === editingPatientId ? { ...p, ...updatedData } : p
            ));
          }}
        />
      </div>

      <div className="standard-container">
            {/* move these inline styles to css */}
            {showOnlyPinned && ( 
              <div className="section-heading-row">
                <span className="section-heading" style={{ cursor: "pointer" }} onClick={() => navigate("/patients")}>
                  Active Patients
                </span>
                <Tooltip title="View All Patients" arrow>
                  <SquareChevronRight size={18} style={{ cursor: "pointer", color: "var(--color-medium-blue)" }} onClick={() => navigate("/patients")} />
                </Tooltip>
              </div>
            )}
            {isLoading && (
              <Box className="table-loading-bar">
                <LinearProgress />
              </Box>
            )}
            <table className="standard-table patients-list-table">
              <thead>
                <tr>
                  {showSortControls ? (
                    <>
                      <th className="column-15" onClick={() => handleSort("name")} style={{ cursor: "pointer", userSelect: "none" }}>
                        Name {getSortIcon("name", sortField, sortDirection)}
                      </th>
                      <th className="column-10" onClick={() => handleSort("date")} style={{ cursor: "pointer", userSelect: "none" }}>
                        Last Session {getSortIcon("date", sortField, sortDirection)}
                      </th>
                      <th className="column-50"></th>
                    </>
                  ) : (
                    <>
                      <th className="column-3"></th>
                      <th className="column-5"></th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentPatients.length === 0 && isLoading ? (
                  <tr>
                    <td colSpan={showSortControls ? "3" : "2"} style={{ textAlign: "left", fontSize: "var(--text-base)", fontWeight: "300", padding: "20px" }}>
                      Loading...
                    </td>
                  </tr>
                ) : currentPatients.length === 0 ? (
                  <tr>
                    <td colSpan={showSortControls ? "3" : "2"} style={searchQuery ? { textAlign: "center", padding: "20px" } : showOnlyPinned ? { textAlign: "left", fontSize: "var(--text-2xl)", fontWeight: "600", padding: "20px" } : { textAlign: "center" }}>
                      {renderEmptyState()}
                    </td>
                  </tr>
                ) : (
                  currentPatients.map((patient) => (
                    <tr 
                      key={patient.id} 
                      className="userback-ignore"
                      style={{
                        opacity: fadingPatients[patient.id] ? 0.3 : 1,
                        transition: "opacity 0.5s ease-in-out"
                      }}
                    >
                      <td onClick={() => handlePatientClick(patient)} style={{ cursor: "pointer" }}>
                        <span className="table-primary-text userback-ignore patients-name-cell">
                          {patient.lastName}, {patient.firstName}
                          {patient.isShared && (
                            <Tooltip
                              title={`Shared with you (${
                                patient.shareInfo?.accessLevel === 'view' ? 'View only' :
                                patient.shareInfo?.accessLevel === 'view_add' ? 'View + Add' :
                                patient.shareInfo?.accessLevel === 'view_add_private' ? 'View + Add (Private)' :
                                'Full access'
                              })`}
                              arrow
                            >
                              <span
                                className="patients-shared-inline-icon"
                                aria-label="Shared patient"
                                style={{ display: "inline-flex", alignItems: "center", marginLeft: 6, color: "var(--color-info, #2196f3)" }}
                              >
                                <Share2 size={12} />
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title={patient.pin ? "Unpin from Dashboard" : "Pin to Dashboard"} arrow>
                            <button
                              onClick={(e) => { e.stopPropagation(); handlePinClick(patient); }}
                              className={`patients-pin-btn${patient.pin ? " pinned" : ""}${pinnedEffect[patient.id] ? " animating" : ""}`}
                              aria-label={patient.pin ? "Unpin from Dashboard" : "Pin to Dashboard"}
                            >
                              <Pin size={12} />
                            </button>
                          </Tooltip>
                          <Tooltip title="Record session" arrow>
                            <button
                              className="patients-record-btn"
                              onClick={(e) => handleRecordClick(e, patient)}
                              aria-label="Record session for this patient"
                            >
                              <Mic size={12} />
                            </button>
                          </Tooltip>
                        </span>
                        {/* Mobile-only second row: demographics under the name
                            (desktop shows these in the details column instead) */}
                        <div className="patients-name-demographics userback-ignore">
                          {patient.dateOfBirth ? `${patient.dateOfBirth} | Age ${calculateAge(patient.dateOfBirth)}` : ""}
                          {patient.dateOfBirth && patient.pronoun ? " | " : ""}
                          {patient.pronoun ? capitalizePronoun(patient.pronoun) : ""}
                        </div>
                      </td>
                      {showSortControls && (
                        <>
                          <td className="userback-ignore">
                            <span className="patient-details-small">{patient.latestDocument?.documentDate || patient.document?.documentDate || ""}</span>
                            {showContactFrequency && patient.contactFrequency && (() => {
                              const createdAt = patient.createdAt?.toDate ? patient.createdAt.toDate() : patient.createdAt?.seconds ? new Date(patient.createdAt.seconds * 1000) : patient.createdAt instanceof Date ? patient.createdAt : null;
                              const isOldEnough = createdAt && (Date.now() - createdAt.getTime() > 28 * 24 * 60 * 60 * 1000);
                              return isOldEnough ? (
                                <div className="contact-frequency">
                                  {patient.contactFrequency.frequency.charAt(0).toUpperCase() + patient.contactFrequency.frequency.slice(1)} contact
                                </div>
                              ) : null;
                            })()}
                          </td>
                          <td>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                {patient.dateOfBirth || patient.pronoun ? (
                                  <span className="patient-details-small userback-ignore">
                                    {patient.dateOfBirth ? `${patient.dateOfBirth} | Age ${calculateAge(patient.dateOfBirth)}` : ""}
                                    {patient.dateOfBirth && patient.pronoun ? " | " : ""}
                                    {patient.pronoun ? capitalizePronoun(patient.pronoun) : ""}
                                  </span>
                                ) : (
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingPatientId(patient.id);
                                      setEditPatientOpen(true);
                                    }}
                                    className="update-patient-chip"
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-badge-purple-hover)'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-badge-purple-bg)'}
                                  >
                                    <UserRoundPen size={12} />
                                    Update
                                  </button>
                                )}
                              </div>
                              <div className="badge-wrapper userback-ignore">
                                {flags?.showSeverity && patient.overallSeverity?.overallSeverityTrend && <div className="overall-severity-trend">{getTrendIcon(patient.overallSeverity.overallSeverityTrend)}</div>}
                                {renderPatientTags(patient, flags, tagWeight)}
                                {renderRiskBadge(patient, flags)}
                              </div>
                            </div>
                            <div className="summary-overview userback-ignore">{renderHistorySummary(patient)}</div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="pagination-controls">
              {filteredPatients.length > documentsPerPage && (
                <Pagination count={pageCount} page={visiblePage} onChange={handlePageChange} className="pagination" color="primary" variant="outlined" shape="rounded" size="small" />
              )}
            </div>
      </div>
    </div>
  );
};

export default PatientsList;
